import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  SITE_URL,
  canonicalUrlFor,
  isNoIndexPath,
} from 'constants/seo';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  description:
    'Greether connects women travelers with verified local female guides — Greeters — so they can explore any destination safely and authentically.',
  sameAs: [
    'https://www.instagram.com/greet.her',
    'https://www.facebook.com/greetherhere',
    'https://www.linkedin.com/company/greether',
    'https://twitter.com/greether_app',
  ],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: 'en',
  publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
};

/**
 * SEO a nivel sitio. Se monta una sola vez dentro del router y aplica a todas
 * las rutas:
 *
 * - canonical self-referencial sobre el host canónico (www), para que las
 *   variantes con query string, barra final o sin www no cuenten como
 *   duplicados.
 * - JSON-LD de marca (Organization + WebSite).
 *
 * Una página puede sobreescribir el canonical declarando el suyo: react-helmet
 * usa `rel` como clave de deduplicación para canonical, así que el `<Helmet>`
 * más profundo del árbol gana. Lo usa /booking-step-one, que canonicaliza hacia
 * la landing del país en vez de hacia sí misma.
 */
const SiteSeo = () => {
  const { pathname } = useLocation();
  const canonical = canonicalUrlFor(pathname);
  const noindex = isNoIndexPath(pathname);

  return (
    <Helmet>
      <link rel="canonical" href={canonical} />
      <meta property="og:url" content={canonical} />
      {noindex ? <meta name="robots" content="noindex, follow" /> : <></>}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
    </Helmet>
  );
};

export default SiteSeo;
